import { AppBar, Menu, Tab } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey, TabLargeStyle, TimelineClassKey } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdAuthor, CmdList, CmdMyPub, CmdPoll, CmdPost, CmdPublishEndDate, CmdPublishStartDate, CmdQuestion, CmdSecondTopic, CmdWithFile, CmdWithImage, CmdWithVideo, CommandLabels } from "components/AdvFilter/CommonCommands";
import { AppBarTabs } from "components/AppTabs";
import ButtonOrderBy from "components/Buttons/ButtonOrderBy";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { nprop } from "lib/LibUtils";
import { PublicationType } from "models/ApiEnums";
import { FeedAttachType, FeedTopicCategory, FeedTopicFilter, FeedTopicOrderBy } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import FeedTopicPublications from "views/Timeline/Components/FeedTopicPublications";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    advFilters: AdvFilterResult[];
    filter: FeedTopicFilter;
    orderBy: FeedTopicOrderBy;
    orderByMenuAnchorEl: any;
}

interface IProps {
    topicId: string;
    fullView: boolean;
}

type Props = IProps & IStateProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class FeedTopic extends React.Component<Props, IState> {

    private advFilterRef: AdvFilterComponent;

    constructor(props: Props) {
        super(props);
        this.state = {
            advFilters: [],
            filter: null,
            orderBy: null,
            orderByMenuAnchorEl: null,
        };
    }


    //#region FILTER

    ///
    private get commands4Read(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdMyPub, displayName: cmdLabels[CmdMyPub], group: 1 },
            { keyword: CmdAuthor, displayName: cmdLabels[CmdAuthor], picker: AdvFilterPicker.User, canRepeat: true, group: 1 },
            { keyword: CmdSecondTopic, displayName: cmdLabels[CmdSecondTopic], picker: AdvFilterPicker.Topic, canRepeat: true, group: 1 },
            { keyword: PublicationType[PublicationType.Post], displayName: cmdLabels[CmdPost], group: 2 },
            { keyword: PublicationType[PublicationType.Question], displayName: cmdLabels[CmdQuestion], group: 2 },
            { keyword: PublicationType[PublicationType.List], displayName: cmdLabels[CmdList], group: 2 },
            { keyword: PublicationType[PublicationType.Poll], displayName: cmdLabels[CmdPoll], group: 2 },
            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo], group: 3 },
            { keyword: CmdWithImage, displayName: cmdLabels[CmdWithImage], group: 3 },
            { keyword: CmdWithFile, displayName: cmdLabels[CmdWithFile], group: 3 },
            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar, group: 4 },
            { keyword: CmdPublishEndDate, displayName: cmdLabels[CmdPublishEndDate], picker: AdvFilterPicker.Calendar, group: 4 }
        ];
    }

    ///
    private get commandsAnswer(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdMyPub, displayName: cmdLabels[CmdMyPub], group: 1 },
            { keyword: CmdAuthor, displayName: cmdLabels[CmdAuthor], picker: AdvFilterPicker.User, canRepeat: true, group: 1 },
            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar, group: 2 },
            { keyword: CmdPublishEndDate, displayName: cmdLabels[CmdPublishEndDate], picker: AdvFilterPicker.Calendar, group: 2 }
        ];
    }

    ///
    private getDefaultOrderBy(category: FeedTopicCategory) {
        if (category == FeedTopicCategory.Read) return FeedTopicOrderBy.Recent;
        else if (category == FeedTopicCategory.Answer) return FeedTopicOrderBy.Priority;
        return FeedTopicOrderBy.Best;
    }

    ///
    private getNewFilter(category: FeedTopicCategory): FeedTopicFilter {
        return {
            topicId: this.props.topicId,
            secondTopicId: null,
            category: category,
            authors: null,
            attachTypes: null,
            endDate: null,
            myPublication: null,
            publicationTypes: null,
            searchText: null,
            startDate: null,
        };
    }

    ///
    private resetFilter() {
        const filter = this.getNewFilter(FeedTopicCategory.Read);

        this.setState({
            filter,
            advFilters: [],
            orderBy: this.getDefaultOrderBy(filter.category)
        }, () => this.advFilterRef && this.advFilterRef.resetFilter());
    }

    ///
    private applyOrder = (orderBy: FeedTopicOrderBy) => {
        this.setState({ orderBy, orderByMenuAnchorEl: null });
    };

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        const filter = this.getNewFilter(this.state.filter.category);

        if (searchText.length > 0 || advFilters.length > 0) {
            filter.searchText = searchText.length == 0 ? null : searchText;

            const authors = nprop(nprop(advFilters.find(x => x.keyword == CmdAuthor)).value, null);
            filter.authors = authors == null ? null : authors.split(',');

            const secondTopic = nprop(nprop(advFilters.find(x => x.keyword == CmdSecondTopic)).value, null);
            filter.secondTopicId = secondTopic == null ? null : secondTopic;

            filter.myPublication = advFilters.findIndex(x => x.keyword == CmdMyPub) > -1;

            filter.publicationTypes = [];
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Post]) > -1) filter.publicationTypes.push(PublicationType.Post);
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.List]) > -1) filter.publicationTypes.push(PublicationType.List);
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Question]) > -1) filter.publicationTypes.push(PublicationType.Question);
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Poll]) > -1) filter.publicationTypes.push(PublicationType.Poll);
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Survey]) > -1) filter.publicationTypes.push(PublicationType.Survey);
            if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Course]) > -1) filter.publicationTypes.push(PublicationType.Course);
            if (filter.publicationTypes.length == 0) filter.publicationTypes = null;

            filter.attachTypes = [];
            if (advFilters.findIndex(x => x.keyword == CmdWithVideo) > -1) filter.attachTypes.push(FeedAttachType.Video);
            if (advFilters.findIndex(x => x.keyword == CmdWithImage) > -1) filter.attachTypes.push(FeedAttachType.Image);
            if (advFilters.findIndex(x => x.keyword == CmdWithFile) > -1) filter.attachTypes.push(FeedAttachType.File);
            if (filter.attachTypes.length == 0) filter.attachTypes = null;

            var dt = advFilters.find(x => x.keyword == CmdPublishStartDate);
            filter.startDate = dt ? (dt.value as Date).putStartOfDay() : null;

            dt = advFilters.find(x => x.keyword == CmdPublishEndDate);
            filter.endDate = dt ? (dt.value as Date).putEndOfDay() : null;
        }
        this.setState({ advFilters, filter });
    };

    ///
    private renderAdvFilter(): JSX.Element {
        const { filter: { category } } = this.state;

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <div>
                        {this.renderMenuBar()}
                    </div>

                    {
                        category != FeedTopicCategory.BestPeople &&
                        <MuiFilterAdvancedContainer>
                            <AdvFilter
                                id="advfilter"
                                commands={category == FeedTopicCategory.Read ? this.commands4Read : this.commandsAnswer}
                                innerRef={(ref) => this.advFilterRef = ref}
                                placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                                placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                                title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                                selectedCommands={this.state.advFilters}
                                onUpdateSelection={this.handleUpdateFilter}
                            />
                            <MuiPushRight>
                                <ButtonOrderBy
                                    onClick={
                                        (e) => {
                                            this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                        }
                                    }
                                />
                                <Menu
                                    anchorEl={this.state.orderByMenuAnchorEl}
                                    open={this.state.orderByMenuAnchorEl != null}
                                    onClose={
                                        () => {
                                            this.setState({ orderByMenuAnchorEl: null });
                                        }
                                    }
                                >
                                    {this.renderOrderBy()}
                                </Menu>
                            </MuiPushRight>
                        </MuiFilterAdvancedContainer>
                    }
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    //#endregion

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        var newFilter = Object.assign({}, this.state.filter);
        newFilter.category = value;

        this.setState({
            filter: newFilter,
            orderBy: this.getDefaultOrderBy(newFilter.category)
        });
    };

    ///
    private renderMenuBar(): JSX.Element {
        return (
            <AppBar position="static" color="default" classes={{ root: this.props.classes.appTabFilterInsideRoot }}>
                <AppBarTabs centered={true} value={this.state.filter.category} onChange={this.handleTabSelection}>
                    <Tab value={FeedTopicCategory.Read} label={this.props.t<string>("Common.Feed.Topic.TabRead")} style={TabLargeStyle} disabled={this.props.feed.loading} />
                    <Tab value={FeedTopicCategory.Answer} label={this.props.t<string>("Common.Feed.Topic.TabAnswer")} style={TabLargeStyle} disabled={this.props.feed.loading}/>
                </AppBarTabs>
            </AppBar>
        );
    }

    ///
    private renderOrderBy() {

        const { filter, orderBy } = this.state;

        if (filter.category == FeedTopicCategory.Read)
            return [
                <MuiMenuItemChecked key="MostRecent" text={this.props.t("Common.Filter.Toolbar.OrderBy.MostRecent")} value={FeedTopicOrderBy.Recent} currentSelection={orderBy} onClick={this.applyOrder} />,
                <MuiMenuItemChecked key="MostLiked" text={this.props.t("Common.Filter.Toolbar.OrderBy.MostLiked")} value={FeedTopicOrderBy.Best} currentSelection={orderBy} onClick={this.applyOrder} />,
                <MuiMenuItemChecked key="MostCommented" text={this.props.t("Common.Filter.Toolbar.OrderBy.MostCommented")} value={FeedTopicOrderBy.MostComments} currentSelection={orderBy} onClick={this.applyOrder} />
            ];

        if (filter.category == FeedTopicCategory.Answer)
            return [
                <MuiMenuItemChecked key="MostPriority" text={this.props.t("Common.Filter.Toolbar.OrderBy.MostPriority")} value={FeedTopicOrderBy.Priority} currentSelection={orderBy} onClick={this.applyOrder} />,
                <MuiMenuItemChecked key="MostRecent" text={this.props.t("Common.Filter.Toolbar.OrderBy.MostRecent")} value={FeedTopicOrderBy.Recent} currentSelection={orderBy} onClick={this.applyOrder} />
            ];

        return null;
    }

    ///
    public componentDidMount() {
        this.resetFilter();
    }

    ///
    public render() {
        const { classes } = this.props;
        const { filter, orderBy } = this.state;

        if (filter == null) return null;

        return (
            <>
                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>

                {
                    filter.category == FeedTopicCategory.Read || filter.category == FeedTopicCategory.Answer
                        ? <FeedTopicPublications classes={classes} fullView={false} filter={filter} orderBy={orderBy} onClearFilter={() => this.advFilterRef.resetFilter()} />
                        : null
                }
            </>
        );
    }
}

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed,
    };
}

const Timeline = connect<IStateProps>(mapStateToProps)(withSnackbar(withTranslation()(FeedTopic)));

export default Timeline;